import Analytics from '@opencare/analytics';
import { IconCheck } from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Link } from 'react-router-dom';

import { LinkButton } from 'brand-guidelines/Buttons-styled-components';
import { COUNTRY } from 'common/constants';
import { colors } from 'common/styles/style-constants';
import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, useFormValidator } from 'common/Validator/Validator';
import { requestClinic } from 'connect/actions';
import { NPR_ERROR_TYPES } from 'connect/constants';
import { RequestAppointment } from 'connect/saga/handlers/appointment/request-appointment';
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { ValidateReservedAppointmentSlot } from 'connect/saga/handlers/reserved-appointment-slot/validate-slot';
import { selectDataUser } from 'connect/selectors';
import { PARTNER_PATH } from 'containers/App/Routes';
import { Checkbox as FormCheckbox } from 'pages/checkout/components/common/checkbox-styled-component';
import { PrivacyLink, TermsOfServiceLink } from 'pages/checkout/components/common/ExternalLinks';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { DisclaimerText } from 'pages/checkout/components/common/typographic-styled-components';
import { selectRequestedAppointment } from 'pages/checkout/connect/legacy-selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState, PreferredContactMethod, PreferredContactMethods } from 'pages/checkout/connect/types';
import { useIsEmailVerificationSkipped, useIsInNetwork } from 'pages/checkout/hooks/checkoutStepHooks';
import { useProviderOrPracticeDetails } from 'pages/checkout/hooks/useProviderOrPracticeDetails';
import { useIsOfferAllowed, useOfferEligibility } from 'pages/checkout/steps/Confirm/hooks/offer-hooks';
import { usePatientV4RedirectExperiment } from 'pages/checkout/steps/Confirm/hooks/usePatientV4RedirectExperiment';
import { Alert } from 'pages/checkout/steps/Insurance/Alert';
import { ALERT_THEMES } from 'pages/checkout/steps/Insurance/Alert-styled-components';
import { formatClinicAddress } from 'pages/checkout/utils/addressUtils';
import { formatPhoneNumber } from 'pages/checkout/utils/phoneUtils';
import { formatTimeSlot } from 'pages/checkout/utils/timeUtils';
import { getTimeFromStartTime } from 'pages/common/helpers/appointment-slot-helpers';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { selectDataClinic } from 'pages/profile/connect/selectors';

import OpencareRewardsModal from 'pages/results/components/OpencareRewardsModal/OpencareRewardsModal';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useOfferService } from 'services/OfferService';
import { useRedirectionService } from 'services/redirection/RedirectionService';
import { Clinic } from 'types/Clinic';
import { Provider } from 'types/Provider';
import cjAffiliate from 'utils/cjAffiliate';
import { MatchHooks } from 'utils/customHooks/MatchHooks';
import { useCheckoutPatient } from 'utils/customHooks/use-checkout-patient';
import { useMatchQualityTierTrackingData } from 'utils/customHooks/use-match-quality-tier-tracking-data';
import { useIsPartnerUser } from 'utils/customHooks/use-partner-user';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';
import { usePatch } from 'utils/customHooks/usePatch';
import { usePatientV4Login } from 'utils/customHooks/usePatientV4Login';
import { googlePlaceActionLink } from 'utils/google';
import Logger, { useLogger } from 'utils/Logger';

import { FormRow } from '../Insurance/Insurance-styled-components';

import { DetailItem, Details } from './components/DetailItem/DetailItem';
import { ErrorMessage } from './components/ErrorMessage';
import { IneligibleForOffer } from './components/IneligibleForOffer/IneligibleForOffer';
import { NoAvailabilityModal } from './components/NoAvailabilityModal/NoAvailabilityModal';
import { PromoCodeBox } from './components/PromoCodeBox/PromoCodeBox';
import { InsuranceStatusBadge, NoOffersStatusBadge } from './components/StatusBadges/ConfirmStepStatusBadge';
import {
  AcknowledgementsWrapper,
  CheckboxLabeledInput,
  ConfirmStepWrapper,
  DetailsWrapper,
  GiftCardText,
  PracticeDetails,
  PracticeTextWrapper,
  ProfilePhoto,
  StatusBadgesWrapper,
  SubmitRequestButton,
  SubmitRequestWrapper,
  SubmitWrapper,
} from './Confirm-styled-components';
import { getOfferedAppointmentType, getPatientAppUrl } from './helpers';
import { useAppointmentRequestData } from './hooks/request-hooks';

interface RequestedAppointment {
  slotId: string;
  provider: Provider;
}

interface ShouldIgnoreParams {
  showOfferAcknowledgement: boolean;
  isOfferAllowed: boolean;
}

interface SlotValidationResult {
  isSlotAvailable: boolean;
  didCheckFail: boolean;
}

const shouldIgnoreOfferAcknowledgement = ({
  showOfferAcknowledgement,
}: ShouldIgnoreParams) => !showOfferAcknowledgement;

const OFFER_ACKNOWLEDGEMENT_VALIDATOR_CONFIG = createImmutableFormValidatorConfig([
  {
    field: 'acceptsTerms',
    preset: ValidationRulePreset.isTrue,
    shouldIgnore: shouldIgnoreOfferAcknowledgement,
  },
  {
    field: 'acceptsGiftCardOfferedByOpencare',
    preset: ValidationRulePreset.isTrue,
    shouldIgnore: shouldIgnoreOfferAcknowledgement,
  },
  {
    field: 'acceptsNoOffer',
    preset: ValidationRulePreset.isTrue,
    shouldIgnore: ({ isOfferAllowed }: ShouldIgnoreParams) => isOfferAllowed,
  },
]);

const OFFER_OFFERED_BY_OPENCARE_LABEL = 'I understand this gift card is offered by Opencare, not the dental practice. For any questions, I will contact Opencare.';

const OFFER_ELIGIBILITY_ACKNOWLEDGEMENT_THRESHOLD = 50;

const contactOptions = [
  { label: 'Phone Call', contactMethod: PreferredContactMethod.Phone },
  { label: 'SMS', contactMethod: PreferredContactMethod.SMS },
];

const contactOptionToApiPayload = (contactMethods: PreferredContactMethods) =>
  Object.keys(contactMethods)
    .filter((key) => contactMethods[key as PreferredContactMethod]);

const analytics = new Analytics();

export const Confirm = () => {
  const logger = useLogger();

  const dispatch = useDispatch();
  const analyticsService = useAnalyticsService();

  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });

  const {
    isLoading: isLoadingPatientV4Login,
    hasSucceeded: hasPatientV4LoginSucceeded,
  } = usePatientV4Login();

  const {
    shouldRedirectToPatientV4,
    isReady: isPatientV4RedirectExperimentReady,
  } = usePatientV4RedirectExperiment();

  const [isLoadingPatientApp, setIsLoadingPatientApp] = useState(false);

  const funnelStorage = useFunnelStorage();
  const redirectionService = useRedirectionService();

  const appointmentRequestData = useAppointmentRequestData();
  const { providerId, body: appointmentRequestBody } = appointmentRequestData || {};

  const isOfferAllowed = useIsOfferAllowed();
  const isPartnerUser = useIsPartnerUser();
  const patient = useCheckoutPatient();

  const user = useSelector(selectDataUser);
  const clinic = useSelector<any, Clinic>(selectDataClinic);
  const requestedAppointment = useSelector<any, RequestedAppointment>(selectRequestedAppointment);
  const { slotId } = requestedAppointment;
  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const {
    insurance,
    confirm: confirmState,
  } = checkoutState;

  const requestOfferLoading = useSelector(RequestOffer.loading!.selector);

  const requestAppointmentLoading = useSelector(RequestAppointment.loading!.selector);
  const requestAppointmentError = useSelector(RequestAppointment.error!.selector);
  const newAppointment = useSelector(RequestAppointment.value!.selector);

  const validateSlotLoading = useSelector(ValidateReservedAppointmentSlot.loading!.selector);
  const validateSlotError = useSelector(ValidateReservedAppointmentSlot.error!.selector);
  const validateSlotResult = useSelector(ValidateReservedAppointmentSlot.value!.selector);

  const requestedAppointmentType = getOfferedAppointmentType(requestedAppointment.provider);

  const isEmailVerificationSkipped = useIsEmailVerificationSkipped();

  const [submitting, setSubmitting] = useState(false);
  const validateSlotResolver = useRef<((result: SlotValidationResult) => void) | null>(null);

  const shouldWaitForPatientV4Login = (
    isPatientV4RedirectExperimentReady
    && shouldRedirectToPatientV4
    && isLoadingPatientV4Login
  );
  const isSubmitLoading = (
    validateSlotLoading
    || requestAppointmentLoading
    || requestOfferLoading
    || isLoadingPatientApp
    || !isPatientV4RedirectExperimentReady
    || shouldWaitForPatientV4Login
  );

  // Handle slot validation result
  useEffect(() => {
    if (validateSlotLoading || !validateSlotResolver.current) {
      return;
    }

    if (validateSlotError) {
      // Slot validation failed, call reject with the error
      // This will reject the promise started in `handleSlotAvailability`
      validateSlotResolver.current({
        isSlotAvailable: false,
        didCheckFail: true,
      });
      validateSlotResolver.current = null;
      return;
    }

    if (validateSlotResult) {
      // Slot validated, call resolver with the result
      // This will fulfill the promise started in `handleSlotAvailability`
      validateSlotResolver.current({
        isSlotAvailable: validateSlotResult.isSlotAvailable,
        didCheckFail: false,
      });
      validateSlotResolver.current = null;
    }
  }, [validateSlotLoading, validateSlotError, validateSlotResult, validateSlotResolver]);

  const isClinicUS = clinic.address.country === COUNTRY.unitedStates;

  const isInNetwork = useIsInNetwork();

  const selectedSlot = useMemo(() => {
    const slot = clinic.slots?.find((slot: any) => slot.id === slotId);
    if (!slot) {
      return null;
    }
    return slot;
  }, [clinic.slots, slotId]);

  const {
    preferredContactMethods: storedPreferredContactMethods,
    note: storedNote,
  } = confirmState || {};

  const [preferredContactMethods, setPreferredContactMethods] =
    useState<PreferredContactMethods>(storedPreferredContactMethods || []);
  const [note, setNote] = useState(storedNote || '');

  const formattedAddress = useMemo(() => formatClinicAddress(clinic.address), [clinic.address]);
  const formattedTimeSlot = useMemo(() => {
    if (!selectedSlot?.date || !selectedSlot?.time) {
      return '';
    }
    return formatTimeSlot(selectedSlot.date, selectedSlot.time);
  }, [selectedSlot?.date, selectedSlot?.time]);

  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      ...analyticsService.analyticsData,
    });
  });

  const togglePreferredContactMethod = (method: PreferredContactMethod) => {
    if (!method) {
      return;
    }

    setPreferredContactMethods((prev) => {
      const newPreferredContactMethods = { ...prev };
      newPreferredContactMethods[method] = !newPreferredContactMethods[method];
      return newPreferredContactMethods;
    });

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      content: {
        action: 'preferred-contact-toggle',
        contact_method: String(method),
      },
    });
  };

  const preferredAppointmentLabel = formattedTimeSlot
    ? 'Preferred Appointment Time'
    : 'Preferred Appointment Type';

  const requestedAppointmentTypeName = requestedAppointmentType?.appointmentType?.name
    || requestedAppointmentType?.name;
  const showPreferredAppointmentTime = (formattedTimeSlot || requestedAppointmentTypeName) && !isPartnerUser;

  // Handle failed appointment request
  useEffect(() => {
    if (!requestAppointmentError) {
      return;
    }

    if (requestAppointmentError === NPR_ERROR_TYPES.OCS_WRITE_ERROR) {
      setIsNoAvailabilityModalOpen(true);
    }
  }, [requestAppointmentError]);

  const {
    matchQualityTier,
    isInNetwork: isInNetworkForTracking,
    insuranceProviders,
  } = useMatchQualityTierTrackingData({
    clinics: [clinic],
    insuranceProvider: insurance?.provider,
    insurancePlan: insurance?.plan,
  });

  // Handle successful appointment request
  useEffect(() => {
    if (!newAppointment?.id || !submitting || requestAppointmentLoading || requestAppointmentError) {
      return;
    }

    try {
      setSubmitting(false);

      if (!isPartnerUser) {
        setIsLoadingPatientApp(true);
      }

      const { requestData, reservedSlotDetails } = appointmentRequestBody || {};

      analytics.trackConversion({
        pageType: analytics.pageTypes.CHECKOUT,
        pageName: clinic.name,
        name: 'form-booking',
        section: 'confirm',
        content: {
          first: String(patient?.firstName),
          last: String(patient?.lastName),
          birth: String(patient?.dateOfBirth),
          email: String(patient?.email),
          phone: String(patient?.mobilePhone),
          promo_code: String(requestData?.dmProps?.promo_code),
          date: String(reservedSlotDetails?.date),
          time: reservedSlotDetails?.time ? String(getTimeFromStartTime(reservedSlotDetails.time, 'HHmm')) : '',
          reco_appointment_booked: String(true),
          reco_step_reached: 'Booked',
        },
        userId: user?.id,
        patientId: patient?.id,
        source: requestData?.source,
        appointmentId: newAppointment.id,
        practiceId: newAppointment.provider.clinic.id,
        providerId: newAppointment.provider.id,
        partnerId: user?.partner?.id,
        isReturningPatient: !newAppointment.requestData?.visitedBefore,
        funnelConfigId: newAppointment?.match?.funnelConfiguration?.id,
        matchId: newAppointment?.match?.id,
        offerValue: newAppointment?.offer?.cost,
        appointmentPrice: newAppointment.metadata?.billingPrice,
        clinicScheduleRate: newAppointment.metadata?.clinicScheduleRate,
        clinicCompleteRate: newAppointment.metadata?.clinicCompleteRate,
        matchQualityTier,
        isInNetwork: isInNetworkForTracking,
        insuranceProviders,
      });

      cjAffiliate.trackAppointmentConversion(newAppointment);

      // Clear marketing values in funnel now that they've been attached to an appointment
      funnelStorage?.clearMarketingCache();

      // Clear the prefill data.
      funnelStorage?.clearPrefillData();

      googlePlaceActionLink.resetConversionData();
    } catch (error) {
      logger.error('Error tracking conversion', { error });
    } finally {
      if (!isPartnerUser) {
        const shouldLogPatientV4LoginFailure = (
          shouldRedirectToPatientV4
          && !hasPatientV4LoginSucceeded
        );
        if (shouldLogPatientV4LoginFailure) {
          Logger.critical('Could not log into Patient V4 before redirecting to the patient app');
        }

        // This is in the `finally` block because we never want to block the user from being redirected to the patient app
        redirectionService.redirect({
          url: getPatientAppUrl(
            newAppointment.id,
            { shouldRedirectToPatientV4 },
          ),
          reason: 'appointment-confirmation',
        });
      }
    }
  }, [
    logger,
    submitting,
    requestAppointmentError,
    requestAppointmentLoading,
    appointmentRequestBody,
    newAppointment,
    requestedAppointment,
    patient,
    user?.id,
    user?.partner?.id,
    clinic.id,
    clinic.name,
    clinic.address,
    clinic.primaryPhoto,
    funnelStorage,
    redirectionService,
    isPartnerUser,
    shouldRedirectToPatientV4,
    hasPatientV4LoginSucceeded,
    matchQualityTier,
    isInNetworkForTracking,
    insuranceProviders,
  ]);

  const patientName = useMemo(
    () => [patient.firstName, patient.lastName]
      .filter(Boolean)
      .join(' '),
    [patient.firstName, patient.lastName],
  );

  const formattedPhoneNumber = useMemo(() => formatPhoneNumber(patient.mobilePhone), [patient.mobilePhone]);

  const { photoUrl: profilePhotoUrl, providerName, practiceName, showProvider } = useProviderOrPracticeDetails();

  // Show provider name if provider is selected, otherwise show practice name
  const providerNameToShow = showProvider ? providerName : practiceName;

  const { hasActiveOffer, offerValue, offerName, isThirdPartyOffer } = useOfferService();

  const {
    isEligible: isPatientEligibleForOffer,
    denyReason: patientOfferEligibilityDenyReason,
  } = useOfferEligibility();

  const showOfferAcknowledgement = useMemo(
    () => isPatientEligibleForOffer
      && hasActiveOffer
      && offerValue >= OFFER_ELIGIBILITY_ACKNOWLEDGEMENT_THRESHOLD,
    [isPatientEligibleForOffer, hasActiveOffer, offerValue],
  );

  const [isNoAvailabilityModalOpen, setIsNoAvailabilityModalOpen] = useState(false);

  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [acceptsGiftCardOfferedByOpencare, setAcceptsGiftCardOfferedByOpencare] = useState(false);
  const [opencareRewardsModalVisible, setOpencareRewardsModalVisible] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [acceptsNoOffer, setAcceptsNoOffer] = useState(false);

  const offerTermsLabel = useMemo(() => (
    <span>
      I understand that I must do a new patient exam and cleaning
      at the practice to be eligible for the <GiftCardText>${offerValue} gift card</GiftCardText>,
      and that
      {' '}
      <LinkButton
        $isUnderlined
        $isBold
        onClick={() => setOpencareRewardsModalVisible(true)}
      >
        additional requirements apply.
      </LinkButton>
    </span>
  ), [offerValue]);

  const noOfferTermsLabel = useMemo(() => (
    <span>
      I understand this practice does not participate in Opencare’s reward program and that
      I will not receive a reward for this visit.
    </span>
  ), []);

  const offerAcknowledgementFormValidator = useFormValidator(OFFER_ACKNOWLEDGEMENT_VALIDATOR_CONFIG);

  const formValidity = useMemo(() => {
    const validationResult = offerAcknowledgementFormValidator.validate(
      { acceptsTerms, acceptsGiftCardOfferedByOpencare, acceptsNoOffer },
      { showOfferAcknowledgement, isOfferAllowed },
    );

    return validationResult;
  }, [
    acceptsTerms,
    acceptsGiftCardOfferedByOpencare,
    showOfferAcknowledgement,
    offerAcknowledgementFormValidator,
    acceptsNoOffer,
    isOfferAllowed,
  ]);

  const { reason: appointmentReason } = MatchHooks.useAppointmentReason();
  const checkSlotAvailability = useCallback(async () => {
    const slotId = selectedSlot?.id;
    const isAutomaticSlot = selectedSlot?.type === 'Opencare Automatic';
    if (!slotId || isAutomaticSlot) {
      return true;
    }

    // Dispatch the validation action
    dispatch(ValidateReservedAppointmentSlot.createAction({
      providerId,
      slotId,

      // Sending appointment reason is very important for clinics that support live availability.
      // Without it, the result could say the slot is valid when it is not.
      reason: appointmentReason,
    }));

    // resolve will be called in useEffect when the saga completes with the result
    const { isSlotAvailable, didCheckFail } = await new Promise<SlotValidationResult>(
      (resolve) => validateSlotResolver.current = resolve,
    );

    if (didCheckFail) {
      return true;
    }

    // Slot is not available, refresh clinic data from the API and open modal
    // (the modal itself tracks a pageview when it opens)
    if (!isSlotAvailable) {

      // Sending the appointment reason is very important for clinics that support live availability.
      // Without it, the slots for the clinic will include slots that don't match the appointment type.
      dispatch(requestClinic(clinic.id, appointmentReason));
      setIsNoAvailabilityModalOpen(true);
    }

    return isSlotAvailable;
  }, [providerId, selectedSlot, dispatch, clinic, appointmentReason]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitLoading || newAppointment) {
      return;
    }

    if (!formValidity.allValid) {
      setShowValidationErrors(true);
      return;
    }

    if (!appointmentRequestBody) {
      Logger.error('No appointment request body found when attempting to submit NPR');
      return;
    }

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      content: {
        note_provided: String(!!note),
        preferred_contact_phone: String(preferredContactMethods[PreferredContactMethod.Phone] || false),
        preferred_contact_sms: String(preferredContactMethods[PreferredContactMethod.SMS] || false),
        email_verification_skipped: String(isEmailVerificationSkipped),
      },
    });

    const isSlotAvailable = await checkSlotAvailability();
    if (!isSlotAvailable) {
      return;
    }

    patchCheckout({
      confirm: {
        preferredContactMethods,
        note,
      },
    });

    setSubmitting(true);

    // `note` and `contactMethods` are set in this handleSubmit function,
    // so there isn't time for them to be updated in the requestData redux before the request is made
    const newRequestData = {
      note: note || '',
      contactMethods: contactOptionToApiPayload(preferredContactMethods),
    };

    // Avoid mutating the original object
    const updatedAppointmentRequestBody = structuredClone(appointmentRequestBody);
    Object.assign(updatedAppointmentRequestBody.requestData, newRequestData);

    dispatch(RequestAppointment.createAction({
      providerId,
      body: updatedAppointmentRequestBody,
    }));
  }, [
    providerId,
    appointmentRequestBody,
    dispatch,
    formValidity.allValid,
    checkSlotAvailability,
    isSubmitLoading,
    note,
    patchCheckout,
    preferredContactMethods,
    isEmailVerificationSkipped,
    newAppointment,
  ]);

  const renderOfferIneligibilityReason = useCallback(() => {
    // Don't show if there is no offer, or if the patient is eligible
    if (!hasActiveOffer || isPatientEligibleForOffer) {
      return null;
    }

    return <IneligibleForOffer denyReason={patientOfferEligibilityDenyReason} />;
  }, [hasActiveOffer, isPatientEligibleForOffer, patientOfferEligibilityDenyReason]);

  const renderOfferAndAcknowledgement = useCallback(() => {
    if (!showOfferAcknowledgement) {
      return null;
    }

    return (
      <AcknowledgementsWrapper>
        <CheckboxLabeledInput label={offerTermsLabel} htmlFor="offer-terms">
          <FormCheckbox
            id="offer-terms"
            checked={acceptsTerms}
            onChange={(e) => setAcceptsTerms(e.target.checked)}
          />
        </CheckboxLabeledInput>
        {showValidationErrors && formValidity.acceptsTerms?.message && (
          <FormError>{formValidity.acceptsTerms.message}</FormError>
        )}

        <CheckboxLabeledInput label={OFFER_OFFERED_BY_OPENCARE_LABEL} htmlFor="offer-opencare">
          <FormCheckbox
            id="offer-opencare"
            checked={acceptsGiftCardOfferedByOpencare}
            onChange={(e) => setAcceptsGiftCardOfferedByOpencare(e.target.checked)}
          />
        </CheckboxLabeledInput>
        {showValidationErrors && formValidity.acceptsGiftCardOfferedByOpencare?.message && (
          <FormError>{formValidity.acceptsGiftCardOfferedByOpencare.message}</FormError>
        )}
      </AcknowledgementsWrapper>
    );
  }, [
    showOfferAcknowledgement,
    offerTermsLabel,
    acceptsTerms,
    acceptsGiftCardOfferedByOpencare,
    showValidationErrors,
    formValidity,
  ]);

  return (
    <>
      <ConfirmStepWrapper>
        <StepHeader>
          <StepHeaderText>{isPartnerUser ? 'Review Request' : 'Review your Request'}</StepHeaderText>
          <StepHeaderSubtext>
            {isPartnerUser
              ? "Please ensure the information below is correct before submission the patient's request"
              : 'Please ensure the information below is correct before submission to your new dentist'}
          </StepHeaderSubtext>
        </StepHeader>

        <DetailsWrapper>
          {showPreferredAppointmentTime
            && (
              <DetailItem
                label={preferredAppointmentLabel}
                details={[
                  { value: formattedTimeSlot, isSensitive: false },
                  { value: requestedAppointmentTypeName, isSensitive: false },
                ]}
              />
            )}

          <DetailItem label="Practice">
            <PracticeDetails>
              <PracticeTextWrapper>
                <Details
                  details={[
                    { value: providerNameToShow, isSensitive: false },
                    // Show practice name if provider is not selected
                    ...(showProvider ? [{ value: practiceName, isSensitive: false }] : []),
                    { value: formattedAddress, isSensitive: false },
                  ]}
                />
                <StatusBadgesWrapper>
                  {isClinicUS && <InsuranceStatusBadge inNetwork={isInNetwork} />}
                  {!isOfferAllowed && <NoOffersStatusBadge />}
                </StatusBadgesWrapper>
              </PracticeTextWrapper>

              <ProfilePhoto src={profilePhotoUrl} alt="" />
            </PracticeDetails>
          </DetailItem>

          <DetailItem
            label="Contact Details"
            details={[
              { value: patientName },
              { value: String(patient.email || '') },
              { value: formattedPhoneNumber },
            ]}
          />

          <DetailItem
            label={isPartnerUser ? 'Patient prefers to be contacted via:' : 'I prefer to be contacted via:'}
            gap={12}
          >
            <FormRow>
              {contactOptions.map((option) => (
                <CheckboxLabeledInput key={option.contactMethod} label={option.label}>
                  <FormCheckbox
                    checked={preferredContactMethods[option.contactMethod]}
                    onChange={() => togglePreferredContactMethod(option.contactMethod)}
                    disabled={!!newAppointment}
                  />
                </CheckboxLabeledInput>
              ))}
            </FormRow>

            <Input
              type={InputType.TEXTAREA}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notes for the practice"
              disabled={!!newAppointment}
            />
          </DetailItem>
        </DetailsWrapper>

        <SubmitWrapper>
          {isOfferAllowed && isPatientEligibleForOffer && <PromoCodeBox />}

          {renderOfferIneligibilityReason()}

          {renderOfferAndAcknowledgement()}

          {!isOfferAllowed && (
            <AcknowledgementsWrapper>
              {/* If we don't use htmlFor, the label will be associated with the LinkButton in the label */}
              <CheckboxLabeledInput label={noOfferTermsLabel}>
                <FormCheckbox
                  id="no-offer-terms"
                  checked={acceptsNoOffer}
                  onChange={(e) => setAcceptsNoOffer(e.target.checked)}
                />
              </CheckboxLabeledInput>
              {showValidationErrors && formValidity.acceptsNoOffer?.message && (
                <FormError>{formValidity.acceptsNoOffer.message}</FormError>
              )}
            </AcknowledgementsWrapper>
          )}

          <DisclaimerText>
            By requesting an appointment with Opencare, you agree to
            our <TermsOfServiceLink /> and <PrivacyLink />.
          </DisclaimerText>

          {isPartnerUser && newAppointment && (
            <Alert theme={ALERT_THEMES.success} customIcon={<IconCheck />}>
              <div>
                Appointment successfully created for {patient.firstName}.{' '}
                <Link style={{ color: colors.sage7 }} to={PARTNER_PATH}>Click here</Link>
                {' '}to return to the match results.
              </div>
            </Alert>
          )}

          <ErrorMessage
            requestAppointmentError={requestAppointmentError}
          />

          <SubmitRequestWrapper>
            <SubmitRequestButton
              onClick={handleSubmit}
              $loading={isSubmitLoading}
              $disabled={!formValidity.allValid || !!newAppointment}
            >
              Send Request
            </SubmitRequestButton>
          </SubmitRequestWrapper>
        </SubmitWrapper>
      </ConfirmStepWrapper>

      <NoAvailabilityModal
        isOpen={isNoAvailabilityModalOpen}
        onClose={() => setIsNoAvailabilityModalOpen(false)}
      />

      {showOfferAcknowledgement && (
        <OpencareRewardsModal
          offerName={offerName}
          isThirdPartyOffer={isThirdPartyOffer}
          opencareRewardsModalVisible={opencareRewardsModalVisible}
          setOpencareRewardsModalVisible={setOpencareRewardsModalVisible}
        />
      )}
    </>
  );
};
